import React, { useState, useRef, useEffect } from 'react';
import { collection, addDoc, query, orderBy, onSnapshot, limit } from 'firebase/firestore';
import { db } from '../firebase-config';

export default function AIAssistant() {
  const [messages, setMessages] = useState([
    {
      id: 1,
      type: 'assistant',
      content: 'Üdvözlöm! Én vagyok a HoppHomes AI asszisztens. Hogyan segíthetek ma?',
      timestamp: new Date()
    }
  ]);
  const [inputMessage, setInputMessage] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const generateAIResponse = async (userMessage) => {
    setIsTyping(true);

    try {
      // Call Firebase Function for ChatGPT
      const { httpsCallable } = await import('firebase/functions');
      const { functions } = await import('../firebase-config');
      
      const chatGptAssistant = httpsCallable(functions, 'chatGptAssistant');
      const result = await chatGptAssistant({ message: userMessage });
      
      setIsTyping(false);
      return result.data.message || 'Sajnos most nem tudok válaszolni. Kérjük, próbálja újra később!';
    } catch (error) {
      console.error('ChatGPT Firebase Function error:', error);
      setIsTyping(false);
      
      // Fallback to business-specific responses
      const responses = {
        'lead': 'Az elmúlt héten 23 új lead érkezett. A legjobb konverziós arány a mobilház kategóriában van (18.5%). Javaslom a Facebook hirdetések optimalizálását.',
        'kampány': 'Jelenleg 5 aktív kampányod van. A "Tavaszi Mobilház Akció" a legjobban teljesít 24.8%-os megnyitási aránnyal. Szeretnéd optimalizálni a többi kampányt?',
        'bevétel': 'A havi bevétel 2.85M Ft, ami 12%-os növekedés az előző hónaphoz képest. A mobilház értékesítések adják a bevétel 65%-át.',
        'affiliate': 'Jelenleg 47 aktív affiliate partneretek van. A top 10 partner generálja a jutalékok 80%-át. Javaslom új toborzási kampány indítását.',
        'optimalizálás': 'AI elemzés alapján javaslatok: 1) Facebook hirdetések költségvetésének 15%-os növelése, 2) Email kampányok személyre szabása, 3) Mobilház landing oldal A/B tesztelése.',
        'előrejelzés': 'A következő hónapra 340 új lead várható, 2.95M Ft bevétellel. A mobilház kereslet 8%-kal fog nőni a tavaszi szezon miatt.'
      };

      const lowerMessage = userMessage.toLowerCase();
      for (const [keyword, reply] of Object.entries(responses)) {
        if (lowerMessage.includes(keyword)) {
          return reply;
        }
      }

      return 'Mint HoppHomes AI asszisztens, segíthetek lead-ekkel, kampányokkal, bevétellel, affiliate programmal, optimalizálással és előrejelzésekkel kapcsolatos kérdésekben. Mit szeretnél tudni?';
    }
  };

  const handleSendMessage = async () => {
    if (!inputMessage.trim()) return;

    const userMessage = {
      id: Date.now(),
      type: 'user',
      content: inputMessage,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    const currentInput = inputMessage;
    setInputMessage('');

    // Generate AI response
    const aiResponse = await generateAIResponse(currentInput);
    
    const assistantMessage = {
      id: Date.now() + 1,
      type: 'assistant',
      content: aiResponse,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, assistantMessage]);

    // Save to Firebase
    try {
      await addDoc(collection(db, 'ai_conversations'), {
        messages: [userMessage, assistantMessage],
        createdAt: new Date()
      });
    } catch (error) {
      console.error('Error saving conversation:', error);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const quickActions = [
    { text: 'Lead statisztikák', icon: '📊' },
    { text: 'Kampány teljesítmény', icon: '📈' },
    { text: 'Bevétel elemzés', icon: '💰' },
    { text: 'Affiliate jelentés', icon: '👥' },
    { text: 'Optimalizálási javaslatok', icon: '🎯' },
    { text: 'Havi előrejelzés', icon: '🔮' }
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-white flex items-center">
          <svg className="w-6 h-6 mr-2 text-[#e31937]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
          </svg>
          AI Asszisztens
        </h2>
        <div className="flex items-center space-x-2">
          <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
          <span className="text-green-400 text-sm">Online</span>
        </div>
      </div>

      {/* Chat Container */}
      <div className="bg-[#1a1a1a] rounded-xl border border-[#e31937]/20 flex flex-col h-[600px]">
        {/* Messages */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {messages.map((message) => (
            <div
              key={message.id}
              className={`flex ${message.type === 'user' ? 'justify-end' : 'justify-start'}`}
            >
              <div className={`max-w-[80%] rounded-lg p-3 ${
                message.type === 'user'
                  ? 'bg-[#e31937] text-white'
                  : 'bg-[#2a2a2a] text-white border border-gray-600'
              }`}>
                {message.type === 'assistant' && (
                  <div className="flex items-center mb-2">
                    <div className="w-6 h-6 bg-[#e31937] rounded-full flex items-center justify-center mr-2">
                      <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                      </svg>
                    </div>
                    <span className="text-xs text-gray-400">AI Asszisztens</span>
                  </div>
                )}
                <p className="text-sm leading-relaxed">{message.content}</p>
                <p className="text-xs opacity-70 mt-2">
                  {message.timestamp.toLocaleTimeString()}
                </p>
              </div>
            </div>
          ))}
          
          {isTyping && (
            <div className="flex justify-start">
              <div className="bg-[#2a2a2a] text-white border border-gray-600 rounded-lg p-3 max-w-[80%]">
                <div className="flex items-center space-x-2">
                  <div className="flex space-x-1">
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"></div>
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{animationDelay: '0.1s'}}></div>
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{animationDelay: '0.2s'}}></div>
                  </div>
                  <span className="text-xs text-gray-400">AI gondolkodik...</span>
                </div>
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Quick Actions */}
        <div className="p-4 border-t border-gray-700">
          <p className="text-gray-400 text-xs mb-2">Gyors műveletek:</p>
          <div className="flex flex-wrap gap-2 mb-3">
            {quickActions.map((action, index) => (
              <button
                key={index}
                onClick={() => setInputMessage(action.text)}
                className="bg-[#2a2a2a] hover:bg-[#3a3a3a] border border-gray-600 rounded-full px-3 py-1 text-xs text-white transition-colors flex items-center space-x-1"
              >
                <span>{action.icon}</span>
                <span>{action.text}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Input */}
        <div className="p-4 border-t border-gray-700">
          <div className="flex space-x-2">
            <textarea
              value={inputMessage}
              onChange={(e) => setInputMessage(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Írj egy üzenetet az AI asszisztensnek..."
              className="flex-1 bg-[#2a2a2a] border border-gray-600 rounded-lg px-3 py-2 text-white placeholder-gray-400 resize-none focus:outline-none focus:ring-2 focus:ring-[#e31937] focus:border-transparent"
              rows={2}
            />
            <button
              onClick={handleSendMessage}
              disabled={!inputMessage.trim() || isTyping}
              className="bg-[#e31937] hover:bg-[#b91c1c] disabled:opacity-50 disabled:cursor-not-allowed text-white px-4 py-2 rounded-lg transition-colors flex items-center justify-center"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
              </svg>
            </button>
          </div>
        </div>
      </div>

      {/* AI Capabilities */}
      <div className="bg-[#1a1a1a] rounded-xl p-6 border border-[#e31937]/20">
        <h3 className="text-white font-semibold mb-4">AI Képességek</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <div className="bg-[#2a2a2a] rounded-lg p-4">
            <div className="flex items-center mb-2">
              <span className="text-2xl mr-2">📊</span>
              <span className="text-white font-medium">Adatelemzés</span>
            </div>
            <p className="text-gray-400 text-sm">Valós idejű statisztikák és trendek elemzése</p>
          </div>

          <div className="bg-[#2a2a2a] rounded-lg p-4">
            <div className="flex items-center mb-2">
              <span className="text-2xl mr-2">🎯</span>
              <span className="text-white font-medium">Optimalizálás</span>
            </div>
            <p className="text-gray-400 text-sm">Kampányok és folyamatok javítási javaslatok</p>
          </div>

          <div className="bg-[#2a2a2a] rounded-lg p-4">
            <div className="flex items-center mb-2">
              <span className="text-2xl mr-2">🔮</span>
              <span className="text-white font-medium">Előrejelzések</span>
            </div>
            <p className="text-gray-400 text-sm">Valós adatokon alapuló jövőbeli trendek</p>
          </div>

          <div className="bg-[#2a2a2a] rounded-lg p-4">
            <div className="flex items-center mb-2">
              <span className="text-2xl mr-2">💡</span>
              <span className="text-white font-medium">Tanácsadás</span>
            </div>
            <p className="text-gray-400 text-sm">Üzleti döntések támogatása AI insights-tal</p>
          </div>

          <div className="bg-[#2a2a2a] rounded-lg p-4">
            <div className="flex items-center mb-2">
              <span className="text-2xl mr-2">🤖</span>
              <span className="text-white font-medium">Automatizálás</span>
            </div>
            <p className="text-gray-400 text-sm">Ismétlődő feladatok automatikus elvégzése</p>
          </div>

          <div className="bg-[#2a2a2a] rounded-lg p-4">
            <div className="flex items-center mb-2">
              <span className="text-2xl mr-2">📈</span>
              <span className="text-white font-medium">Jelentések</span>
            </div>
            <p className="text-gray-400 text-sm">Részletes teljesítmény jelentések generálása</p>
          </div>
        </div>
      </div>
    </div>
  );
}
